import { useTranslation } from "react-i18next"
import { RecursiveNavLink } from "./recursive-nav-link"
import { AttachmentUploadContext } from "../../../attachment-api/components/attachment-upload-context"
import { useSuspenseQueryGet } from "../../../typed-client/hooks/use-suspense-query-get"
import { useQueryGet } from "@/typed-client/hooks/use-query-get"
import { NavTreeRoute } from "@/@client/Theasoft_ApiServer_NavigationTree"
import { TreeNode } from "@/@client/Theasoft_ApiServer__Utils_TreeBase"
import { useGetUnreadCount } from "@/orval/gen"
import { useCountOverdueRemindersForCurrentUser } from "@/orval/gen/api/reminder/reminder"

interface Props {
  navigationTree: TreeNode<NavTreeRoute>[]
  onNodeClick?: () => void
  iconsOnly?: boolean
}

// We could use our generic TreeRenderer for the navTree, but right now
// this works fine and the effort needed to replicate the routeMatching/opening logic would
// outweigh the gains.
export function NavigationTree({
  iconsOnly,
  onNodeClick,
  navigationTree,
}: Props) {
  const { t } = useTranslation("app_server")

  const { uploadQueue } = React.useContext(AttachmentUploadContext)

  const { data: authStatus } = useSuspenseQueryGet(
    "/api/Authentication/GetAuthStatus",
    null
  )

  const { data: features } = useQueryGet(
    "/api/Settings/GetFeatureSettings",
    null,
    {
      enabled: authStatus.loggedIn,
    }
  )

  const { data: authenticatedUser } = useQueryGet(
    "/api/User/GetAuthenticatedUser",
    null,
    { enabled: authStatus.loggedIn }
  )

  const { data: unreadCount } = useGetUnreadCount({
    query: {
      enabled: !!features?.chatEnabled,
    },
  })

  const { data: openRemindersCount } = useCountOverdueRemindersForCurrentUser()

  return authStatus.loggedIn ? (
    <RecursiveNavLink
      nodes={navigationTree}
      t={t}
      iconsOnly={iconsOnly}
      onNodeClick={onNodeClick}
      uploadQueue={uploadQueue}
      unreadMessageCount={unreadCount}
      currentUserId={authenticatedUser?.userId}
      openRemindersCount={openRemindersCount}
    />
  ) : (
    // in anonymous modus there are not many navigation items, so we flatten everything and remove groups
    <RecursiveNavLink
      nodes={[
        // remove top level groups
        ...navigationTree.filter(
          (v) => v.children === null || v.children.length === 0
        ),
        // unwrap one level of children
        ...navigationTree.flatMap((v) => v.children),
      ]}
      t={t}
      iconsOnly={iconsOnly}
      onNodeClick={onNodeClick}
      uploadQueue={uploadQueue}
      unreadMessageCount={unreadCount}
    />
  )
}
